<template>
  <v-container class="py-6" fluid>
    <v-row>
      <!-- LEFT PANEL: Ultimate Designer with exactly the tabs of Nathan Dev + Plantilla EJS -->
      <v-col cols="12" lg="8">
        <v-card class="rounded-lg elevation-3 border-top-accent h-100 d-flex flex-column pa-6">
          <v-card-title class="d-flex align-center pb-0">
            <v-icon left color="primary" size="32">mdi-palette-swatch-outline</v-icon>
            <span class="text-h5 font-weight-bold ml-2">Configuración Avanzada de Templates</span>
            <HelpHint class="ml-2" text="Editor avanzado de plantilla documental. Cambios aquí afectan la generación final de PDFs y remitos." />
          </v-card-title>
          <v-card-subtitle class="text-subtitle-1 mb-4">
            Replicación idéntica del módulo de templates de Nathan Dev.
          </v-card-subtitle>

          <!-- PRESETS SELECTOR -->
          <v-row class="px-2 mb-2 align-center">
            <v-col cols="12" md="8">
              <v-select
                v-model="selectedPreset"
                :items="presets"
                item-text="name"
                item-value="value"
                label="Importar Template / Preset"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-auto-fix"
                class="mb-2"
                @change="applyPreset"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <v-btn text color="primary" @click="resetToInitial" small>
                <v-icon left>mdi-refresh</v-icon> Reestablecer
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="mb-4"></v-divider>

          <!-- The detailed 8 sub-tabs from Nathan Dev -->
          <v-tabs v-model="tabActiva" background-color="primary" dark class="rounded-t-lg mb-4">
            <v-tab><v-icon left>mdi-table-column</v-icon> Columnas</v-tab>
            <v-tab><v-icon left>mdi-form-textbox</v-icon> Campos</v-tab>
            <v-tab><v-icon left>mdi-office-building</v-icon> Datos Empresa</v-tab>
            <v-tab><v-icon left>mdi-palette</v-icon> Estilos</v-tab>
            <v-tab><v-icon left>mdi-text</v-icon> Textos</v-tab>
            <v-tab><v-icon left>mdi-view-dashboard</v-icon> Layout</v-tab>
            <v-tab><v-icon left>mdi-cog</v-icon> Avanzado</v-tab>
            <v-tab><v-icon left>mdi-code-braces</v-icon> Plantilla EJS</v-tab>
          </v-tabs>

          <v-form ref="form" v-model="valid" class="flex-grow-1">
            <v-tabs-items v-model="tabActiva" class="pa-2">
              
              <!-- Tab 1: Columnas -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-3">Editar Columnas de Tabla</div>
                <v-row dense v-for="(col, i) in template.tabla_config.columnas" :key="i" class="align-center mb-1">
                  <v-col cols="3">
                    <v-checkbox v-model="col.visible" :label="col.label" color="primary" hide-details class="mt-0"></v-checkbox>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field v-model="col.label" label="Etiqueta" outlined dense hide-details></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field v-model="col.width" label="Ancho" outlined dense hide-details placeholder="Ej: 15%"></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-select v-model="col.align" :items="['left', 'center', 'right']" label="Alineación" outlined dense hide-details></v-select>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 2: Campos -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-3">Campos de secciones del documento</div>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <div class="text-subtitle-2 mb-2 font-weight-bold">Sección Cliente</div>
                    <v-row dense v-for="(c, idx) in template.campos_config.cliente" :key="'cli'+idx">
                      <v-col cols="12">
                        <v-checkbox v-model="c.visible" :label="c.label" color="primary" hide-details class="mt-0 mb-1"></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="text-subtitle-2 mb-2 font-weight-bold">Sección Entrega y Transporte</div>
                    <v-row dense v-for="(c, idx) in template.campos_config.entrega" :key="'ent'+idx">
                      <v-col cols="12">
                        <v-checkbox v-model="c.visible" :label="c.label" color="primary" hide-details class="mt-0 mb-1"></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 3: Datos de Empresa -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-3">Datos de la Empresa</div>
                <v-row dense>
                  <v-col cols="12" md="6" v-for="(v, k) in template.estilos_config.datosEmpresa" :key="k" v-if="k !== 'estiloEncabezado'">
                    <v-text-field v-model="v.valor" :label="k.toUpperCase()" outlined dense class="mb-2" hide-details>
                      <template v-slot:append>
                        <v-checkbox v-model="v.visible" hide-details class="mt-0"></v-checkbox>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select v-model="template.estilos_config.datosEmpresa.estiloEncabezado.alineacion" :items="['left', 'center', 'right']" label="Alineación Cabecera" outlined dense hide-details></v-select>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 4: Estilos -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-3">Estilos, Colores y Tipografía</div>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field v-model="template.estilos_config.colores.accentColor" label="Color de Acento" outlined dense class="mb-3">
                      <template v-slot:append>
                        <div :style="{ width: '20px', height: '20px', backgroundColor: template.estilos_config.colores.accentColor || '#0066cc', border: '1px solid #ccc', borderRadius: '4px' }"></div>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="template.estilos_config.colores.headerBackground" label="Fondo Cabecera" outlined dense class="mb-3"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="template.estilos_config.fuentes.tamanoBase" label="Tamaño de Fuente Base" outlined dense class="mb-3"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="template.estilos_config.fuentes.tamanoTitulos" label="Tamaño Títulos" outlined dense class="mb-3"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="template.estilos_config.margenes.top" label="Margen Superior" outlined dense class="mb-3"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="template.estilos_config.margenes.left" label="Margen Izquierdo" outlined dense class="mb-3"></v-text-field>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 5: Textos -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-3">Etiquetas y Leyendas</div>
                <v-row dense>
                  <v-col cols="12">
                    <v-text-field v-model="template.textos_config.titulos.detalle" label="Título Detalle" outlined dense class="mb-2"></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea v-model="template.textos_config.leyendas.pieDocumento" label="Leyenda Pie de Documento" outlined dense rows="3"></v-textarea>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 6: Layout -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-3">Organización Visual del Cabezal</div>
                <v-row dense>
                  <v-col cols="12">
                    <v-select v-model="template.layout_config.header.layout" :items="['left-center-right', 'logo-left-empresa-right']" label="Estilo Encabezado" outlined dense></v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox v-model="template.layout_config.header.enabled" label="Activar Header Completo" color="primary"></v-checkbox>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 7: Avanzado -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-3">Opciones Avanzadas</div>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-checkbox v-model="template.opciones_avanzadas.mostrarCajaR" label="Mostrar caja de letra de documento" color="primary" hide-details class="mb-2 mt-0"></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-checkbox v-model="template.opciones_avanzadas.mostrarCodigoBarras" label="Mostrar código de barras" color="primary" hide-details class="mb-2 mt-0"></v-checkbox>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox v-model="template.opciones_avanzadas.mostrarNumeroPagina" label="Mostrar número de página" color="primary" hide-details class="mb-2 mt-0"></v-checkbox>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 8: Plantilla EJS -->
              <v-tab-item>
                <div class="text-subtitle-1 font-weight-bold mb-2">Plantilla EJS original de Nathan Dev</div>
                <div class="text-caption mb-3" style="color:#64748b;">
                  Modifica directamente la plantilla de diseño HTML/EJS usada para generar el PDF.
                </div>
                <v-textarea
                  v-model="template.html_template"
                  outlined
                  auto-grow
                  rows="22"
                  class="code-editor-textarea text-caption font-italic"
                  style="font-family: monospace; background-color: #1e1e1e; color: #d4d4d4;"
                  hide-details
                ></v-textarea>
              </v-tab-item>

            </v-tabs-items>

            <v-divider class="mt-auto mb-4"></v-divider>

            <div class="d-flex align-center">
              <v-btn color="primary" :loading="saving" @click="save" large elevation="2" class="px-6 font-weight-bold">
                <v-icon left>mdi-content-save-outline</v-icon> Guardar Configuración
              </v-btn>
              <span v-if="successMsg" class="ml-4 success-text font-weight-bold animate-fade">{{ successMsg }}</span>
            </div>
          </v-form>
        </v-card>
      </v-col>

      <!-- RIGHT PANEL: Live Visual Preview exactly like Nathan Dev -->
      <v-col cols="12" lg="4">
        <v-card class="pa-6 rounded-lg elevation-3 h-100 bg-preview d-flex flex-column">
          <v-card-title class="d-flex align-center pb-0">
            <v-icon left color="success" size="32">mdi-eye-outline</v-icon>
            <span class="text-h5 font-weight-bold ml-2">Vista Previa</span>
          </v-card-title>
          <v-card-subtitle class="text-subtitle-1 mb-4">
            Visualización premium del documento final.
          </v-card-subtitle>

          <v-divider class="mb-6"></v-divider>

          <!-- Visual Mockup Container -->
          <div class="flex-grow-1 d-flex flex-column justify-center align-center pa-1">
            <v-card outlined class="paper-page pa-5 d-flex flex-column rounded-lg elevation-4 w-100" style="max-width: 480px; min-height: 580px; background-color: #fff; border: 1px solid #ddd;">
              
              <!-- Top header of the document -->
              <div class="d-flex justify-space-between mb-3" :style="{ paddingTop: template.estilos_config.margenes.top, paddingLeft: template.estilos_config.margenes.left }">
                <div>
                  <h3 :style="{ color: template.estilos_config.colores.accentColor || '#0066cc', fontSize: template.estilos_config.fuentes.tamanoTitulos || '14px', margin: 0, fontWeight: 'bold' }">
                    {{ template.estilos_config.datosEmpresa.razonSocial.visible ? template.estilos_config.datosEmpresa.razonSocial.valor || 'EMPRESA' : 'HERRAMETAL S.A.' }}
                  </h3>
                  <div :style="{ fontSize: template.estilos_config.fuentes.tamanoBase || '11px', color: '#666', marginTop: '4px' }" v-if="template.estilos_config.datosEmpresa.direccion.visible">
                    {{ template.estilos_config.datosEmpresa.direccion.valor || 'Plataforma Operativa' }}
                  </div>
                </div>

                <!-- Middle Box: Letter -->
                <div v-if="template.opciones_avanzadas.mostrarCajaR" class="letter-box d-flex justify-center align-center" :style="{ borderColor: template.estilos_config.colores.accentColor || '#0066cc' }">
                  <span :style="{ color: template.estilos_config.colores.accentColor || '#0066cc', fontSize: '26px', fontWeight: '900' }">
                    R
                  </span>
                </div>

                <!-- Right top column -->
                <div class="text-right" :style="{ fontSize: template.estilos_config.fuentes.tamanoBase || '11px', color: '#333' }">
                  <strong :style="{ color: template.estilos_config.colores.accentColor || '#0066cc' }">Comprobante N°:</strong> 0001-00045612<br>
                  <strong>Fecha:</strong> 01/01/2026<br>
                  <strong v-if="template.estilos_config.datosEmpresa.cuit.visible"><strong>CUIT:</strong> {{ template.estilos_config.datosEmpresa.cuit.valor || '30-12345678-9' }}</strong>
                </div>
              </div>

              <v-divider class="mb-3" :style="{ backgroundColor: template.estilos_config.colores.accentColor || '#0066cc', height: '2px' }"></v-divider>

              <!-- Company details section -->
              <div class="mb-4 d-flex justify-space-between flex-wrap" :style="{ fontSize: template.estilos_config.fuentes.tamanoBase || '11px', color: '#334155' }">
                <div>
                  <div v-if="template.estilos_config.datosEmpresa.localidad.visible"><strong>Localidad:</strong> {{ template.estilos_config.datosEmpresa.localidad.valor || 'CABA' }}</div>
                  <div v-if="template.estilos_config.datosEmpresa.telefono.visible"><strong>Teléfono:</strong> {{ template.estilos_config.datosEmpresa.telefono.valor || '+54 11 2345-6789' }}</div>
                </div>
                <div class="text-right">
                  <div v-if="template.estilos_config.datosEmpresa.email.visible"><strong>Email:</strong> {{ template.estilos_config.datosEmpresa.email.valor || 'ventas@herrametal.com' }}</div>
                  <div v-if="template.estilos_config.datosEmpresa.ingresosBrutos.visible"><strong>I.I.B.B.:</strong> {{ template.estilos_config.datosEmpresa.ingresosBrutos.valor || 'Exento' }}</div>
                </div>
              </div>

              <!-- Client Data Section Mockup -->
              <div v-if="template.campos_config.cliente[0].visible" class="mb-4 pa-3 rounded-lg" style="background-color: #f8fafc; border: 1px solid #e2e8f0; font-size: 11px;">
                <v-row dense>
                  <v-col cols="6"><strong>Cliente:</strong> Cliente de Demostración S.R.L.</v-col>
                  <v-col cols="6" class="text-right"><strong>CUIT:</strong> 30-98765432-1</v-col>
                  <v-col cols="12" v-if="template.campos_config.cliente[1].visible"><strong>Domicilio:</strong> Av. Libertador 4567, CABA</v-col>
                </v-row>
              </div>

              <!-- Visual Items table -->
              <div class="flex-grow-1">
                <v-simple-table dense class="preview-table mb-3">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th v-if="template.tabla_config.columnas[0].visible" :style="{ backgroundColor: template.estilos_config.colores.accentColor || '#0066cc', color: '#fff', width: template.tabla_config.columnas[0].width }">{{ template.tabla_config.columnas[0].label }}</th>
                        <th v-if="template.tabla_config.columnas[1].visible" :style="{ backgroundColor: template.estilos_config.colores.accentColor || '#0066cc', color: '#fff', width: template.tabla_config.columnas[1].width }">{{ template.tabla_config.columnas[1].label }}</th>
                        <th v-if="template.tabla_config.columnas[2].visible" :style="{ backgroundColor: template.estilos_config.colores.accentColor || '#0066cc', color: '#fff', width: template.tabla_config.columnas[2].width }">{{ template.tabla_config.columnas[2].label }}</th>
                        <th v-if="template.tabla_config.columnas[3].visible" :style="{ backgroundColor: template.estilos_config.colores.accentColor || '#0066cc', color: '#fff', width: template.tabla_config.columnas[3].width }">{{ template.tabla_config.columnas[3].label }}</th>
                      </tr>
                    </thead>
                    <tbody style="font-size: 11px; color: #475569;">
                      <tr>
                        <td v-if="template.tabla_config.columnas[0].visible">A12</td>
                        <td v-if="template.tabla_config.columnas[1].visible">Disco de Corte Metal Premium 4.5"</td>
                        <td v-if="template.tabla_config.columnas[2].visible">10</td>
                        <td v-if="template.tabla_config.columnas[3].visible">$850.00</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>

              <!-- Barcode & footer -->
              <div v-if="template.opciones_avanzadas.mostrarCodigoBarras" class="text-center mb-3">
                <v-icon large color="black">mdi-barcode</v-icon>
                <div style="font-size: 8px; letter-spacing: 2px;">*0001000456126*</div>
              </div>

              <v-divider class="mb-3"></v-divider>

              <!-- Footer info -->
              <div class="mt-auto pt-2" :style="{ fontSize: template.estilos_config.fuentes.tamanoBase || '11px' }">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="text-body-2 font-italic" style="color: #64748b;">
                    {{ template.textos_config.leyendas.pieDocumento || '¡Muchas gracias por su compra!' }}
                  </div>
                  <div class="text-right">
                    <strong style="font-size: 16px;" :style="{ color: template.estilos_config.colores.accentColor || '#0066cc' }">Total: $8,500.00</strong>
                  </div>
                </div>
              </div>

            </v-card>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "../services/api";
import HelpHint from "@/components/HelpHint.vue";

export default {
  name: "DocumentDesignConfig",
  components: { HelpHint },
  data() {
    return {
      tabActiva: 0,
      valid: true,
      saving: false,
      successMsg: "",
      selectedPreset: null,
      presets: [
        { name: "Template Estándar (Fibra del Sur)", value: "nathan_fibra" },
        { name: "Template Minimalista", value: "nathan_minimal" },
        { name: "Estilo Moderno (Herrametal)", value: "herra_modern" }
      ],
      template: {
        id: null,
        id_empresa: 284,
        nombre: "Fibra del Sur",
        activo: 1,
        es_default: 0,
        html_template: "",
        tabla_config: {
          columnas: [
            { campo: "codigo", visible: false, label: "Código", width: "15%", align: "left" },
            { campo: "descripcion", visible: true, label: "Descripción", width: "55%", align: "left" },
            { campo: "cantidad", visible: true, label: "Cantidad", width: "15%", align: "center" },
            { campo: "precio", visible: true, label: "Precio", width: "15%", align: "right" }
          ]
        },
        campos_config: {
          cliente: [
            { campo: "razonSocial", label: "Razón Social", visible: true },
            { campo: "direccion", label: "Dirección", visible: true }
          ],
          entrega: [
            { campo: "domicilio", label: "Domicilio", visible: true }
          ]
        },
        estilos_config: {
          colores: { accentColor: "#0066cc", headerBackground: "#e0e0e0" },
          fuentes: { tamanoBase: "11px", tamanoTitulos: "14px" },
          margenes: { top: "35px", left: "35px" },
          datosEmpresa: {
            razonSocial: { visible: true, valor: "FIBRA DEL SUR S.A.", formato: "bold" },
            direccion: { visible: true, valor: "Ruta 36 KM34,5 (1888)", formato: "normal" },
            localidad: { visible: true, valor: "Florencio Varela - Pcia de Bs.As.", formato: "normal" },
            telefono: { visible: true, valor: "Tel.: (011) 2822-9999", formato: "normal" },
            web: { visible: true, valor: "www.gruponathan.com", formato: "normal" },
            cuit: { visible: true, valor: "CUIT: 30-70767482-9", formato: "normal" },
            inicioActividades: { visible: true, valor: "INICIO DE ACTIVIDADES: 12-11-2025", formato: "normal" },
            ingresosBrutos: { visible: true, valor: "IIBB: 901-053013-3", formato: "normal" },
            email: { visible: true, valor: "hola@gruponathan.com", formato: "normal" },
            estiloEncabezado: { alineacion: "left", tamanoFuente: "12px" }
          }
        },
        textos_config: {
          titulos: { detalle: "DETALLE" },
          leyendas: { pieDocumento: "Se acuerda la competencia de la Justicia Nacional en lo Comercial de la Capital Federal. No se aceptan devoluciones sin previo aviso." }
        },
        layout_config: {
          header: { layout: "left-center-right", enabled: true }
        },
        opciones_avanzadas: {
          mostrarCajaR: true,
          mostrarCodigoBarras: true,
          mostrarNumeroPagina: true
        }
      }
    };
  },
  async created() {
    await this.fetchDesigns();
  },
  methods: {
    applyPreset() {
      if (!this.selectedPreset) return;

      if (this.selectedPreset === "nathan_fibra") {
        this.template.estilos_config.datosEmpresa.razonSocial.valor = "Fibra del Sur S.A.";
        this.template.estilos_config.datosEmpresa.direccion.valor = "Ruta 36 KM34,5 (1888)";
        this.template.estilos_config.datosEmpresa.localidad.valor = "Florencio Varela - Pcia de Bs.As.";
        this.template.estilos_config.datosEmpresa.telefono.valor = "(011) 2822-9999";
        this.template.estilos_config.datosEmpresa.email.valor = "hola@gruponathan.com";
        this.template.estilos_config.datosEmpresa.cuit.valor = "30-70767482-9";
        this.template.estilos_config.datosEmpresa.ingresosBrutos.valor = "901-053013-3";
        this.template.estilos_config.datosEmpresa.inicioActividades.valor = "12-11-2025";
        this.template.estilos_config.datosEmpresa.web.valor = "www.gruponathan.com";
        this.template.estilos_config.colores.accentColor = "#0066cc";
        this.template.textos_config.leyendas.pieDocumento = "Se acuerda la competencia de la Justicia Nacional en lo Comercial de la Capital Federal. No se aceptan devoluciones sin previo aviso.";
      } else if (this.selectedPreset === "nathan_minimal") {
        this.template.estilos_config.datosEmpresa.razonSocial.valor = "DISEÑO MINIMALISTA S.R.L.";
        this.template.estilos_config.datosEmpresa.direccion.valor = "Calle Florida 456";
        this.template.estilos_config.datosEmpresa.localidad.valor = "CABA";
        this.template.estilos_config.datosEmpresa.telefono.valor = "+54 11 9876-5432";
        this.template.estilos_config.datosEmpresa.email.valor = "minimal@nathan-dev.com";
        this.template.estilos_config.datosEmpresa.cuit.valor = "30-99999999-9";
        this.template.estilos_config.datosEmpresa.ingresosBrutos.valor = "Exento";
        this.template.estilos_config.datosEmpresa.inicioActividades.valor = "10/10/2020";
        this.template.estilos_config.datosEmpresa.web.valor = "www.minimal-design.com";
        this.template.estilos_config.colores.accentColor = "#1e293b";
        this.template.textos_config.leyendas.pieDocumento = "Este documento no es válido como factura comercial. Documento de control interno.";
      } else if (this.selectedPreset === "herra_modern") {
        this.template.estilos_config.datosEmpresa.razonSocial.valor = "HERRAMETAL S.A.";
        this.template.estilos_config.datosEmpresa.direccion.valor = "San Martín 9182";
        this.template.estilos_config.datosEmpresa.localidad.valor = "Rosario, Santa Fe";
        this.template.estilos_config.datosEmpresa.telefono.valor = "+54 11 4455-6677";
        this.template.estilos_config.datosEmpresa.email.valor = "contacto@herrametal.com";
        this.template.estilos_config.datosEmpresa.cuit.valor = "30-71458921-2";
        this.template.estilos_config.datosEmpresa.ingresosBrutos.valor = "30-71458921-2";
        this.template.estilos_config.datosEmpresa.inicioActividades.valor = "01/01/2022";
        this.template.estilos_config.datosEmpresa.web.valor = "www.herrametal.com";
        this.template.estilos_config.colores.accentColor = "#10b981";
        this.template.textos_config.leyendas.pieDocumento = "¡Gracias por su compra! Por reclamos o devoluciones contactar con soporte técnico.";
      }
    },
    resetToInitial() {
      this.template.estilos_config.datosEmpresa.razonSocial.valor = "HERRAMETAL S.A.";
      this.template.estilos_config.datosEmpresa.direccion.valor = "Ejemplo 123";
      this.template.estilos_config.colores.accentColor = "#1e293b";
    },
    async fetchDesigns() {
      try {
        const res = await api.get("/document-designs");
        if (res.data && res.data.data && res.data.data.length > 0) {
          const d = res.data.data[0];
          
          this.template.id = d.id;
          this.template.id_empresa = d.idEmpresa;
          this.template.nombre = d.nombre;
          this.template.activo = d.activo;
          this.template.es_default = d.esDefault;
          this.template.html_template = d.htmlTemplate;

          if (d.layoutConfig) {
            try {
              this.template.layout_config = typeof d.layoutConfig === 'string' ? JSON.parse(d.layoutConfig) : d.layoutConfig;
            } catch (e) {}
          }
          if (d.camposConfig) {
            try {
              this.template.campos_config = typeof d.camposConfig === 'string' ? JSON.parse(d.camposConfig) : d.camposConfig;
            } catch (e) {}
          }
          if (d.tablaConfig) {
            try {
              this.template.tabla_config = typeof d.tablaConfig === 'string' ? JSON.parse(d.tablaConfig) : d.tablaConfig;
            } catch (e) {}
          }
          if (d.estilosConfig) {
            try {
              this.template.estilos_config = typeof d.estilosConfig === 'string' ? JSON.parse(d.estilosConfig) : d.estilosConfig;
            } catch (e) {}
          }
          if (d.textosConfig) {
            try {
              this.template.textos_config = typeof d.textosConfig === 'string' ? JSON.parse(d.textosConfig) : d.textosConfig;
            } catch (e) {}
          }
          if (d.opcionesAvanzadas) {
            try {
              this.template.opciones_avanzadas = typeof d.opcionesAvanzadas === 'string' ? JSON.parse(d.opcionesAvanzadas) : d.opcionesAvanzadas;
            } catch (e) {}
          }
        }
      } catch (err) {
        console.error("Error fetching designs:", err);
      }
    },
    async save() {
      this.saving = true;
      this.successMsg = "";
      try {
        const payload = {
          id: this.template.id,
          idEmpresa: this.template.id_empresa,
          nombre: this.template.nombre,
          activo: this.template.activo,
          esDefault: this.template.es_default,
          layoutConfig: JSON.stringify(this.template.layout_config),
          camposConfig: JSON.stringify(this.template.campos_config),
          tablaConfig: JSON.stringify(this.template.tabla_config),
          estilosConfig: JSON.stringify(this.template.estilos_config),
          textosConfig: JSON.stringify(this.template.textos_config),
          opcionesAvanzadas: JSON.stringify(this.template.opciones_avanzadas)
        };
        const res = await api.post("/document-designs", payload);
        if (res.data && res.data.success) {
          this.successMsg = "¡Template guardado correctamente!";
          if (res.data.data && res.data.data.id) {
            this.template.id = res.data.data.id;
          }
          setTimeout(() => {
            this.successMsg = "";
          }, 3500);
        }
      } catch (err) {
        console.error("Error saving design:", err);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.border-top-accent {
  border-top: 4px solid var(--v-primary-base, #1976D2);
}
.success-text {
  color: #4caf50;
  font-size: 1rem;
}
.animate-fade {
  animation: fadeIn 0.5s ease;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.bg-preview {
  background-color: #f1f5f9;
}
.paper-page {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}
.letter-box {
  border: 2px solid;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
}
.preview-table th {
  padding: 6px 12px !important;
  font-weight: bold;
}
.preview-table td {
  padding: 6px 12px !important;
}
</style>
